import { Box } from '@factorialco/deprecated-design-system'
import {
  OnePagination,
  useDataCollectionData,
} from '@factorialco/f0-react/dist/experimental'

import type Employee from 'modules/employees/store/models/Employee'
import type SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import type { EmployeeEstimatedPeriodBaseSalary } from 'modules/payroll_bundle/hooks/useEmployeeEstimatedPeriodBaseSalary'
import type { ContractTaxonomyFragment } from 'generated/resources/graphql'

import { SupplementTableRowCell } from './SupplementTableRowCell'

type EmployeeNode = { id: number }

export interface SupplementsCustomVisualizationProps {
  source: unknown
  employeesById: Record<number, Employee>
  supplementsStore: SupplementsFormStore
  loadingSupplements: boolean
  taxonomies: ContractTaxonomyFragment[]
  estimatedEmployeesSalariesByEmployeeId: Record<number, EmployeeEstimatedPeriodBaseSalary>
  itemToHighlight?: { employeeId?: number; supplementId?: number } | null
  employeeExpansionState: Record<string, boolean>
  toggleEmployeeExpansion: (id: string) => void
  toggleTaxonomyCreationModal: (value: boolean) => void
}

/**
 * Custom DataCollection visualization that renders the same data as the old
 * supplements table: one row per employee with their supplement forms.
 * Uses the collection source for filters/search/pagination; no separate table.
 */
export function SupplementsCustomVisualization({
  source,
  employeesById,
  supplementsStore,
  loadingSupplements,
  taxonomies,
  estimatedEmployeesSalariesByEmployeeId,
  itemToHighlight,
  employeeExpansionState,
  toggleEmployeeExpansion,
  toggleTaxonomyCreationModal,
}: SupplementsCustomVisualizationProps) {
  const {
    data: collectionData,
    paginationInfo,
    setPage,
    isLoading,
  } = useDataCollectionData(
    source as Parameters<typeof useDataCollectionData>[0]
  )
  const records: EmployeeNode[] = (collectionData?.records ?? []) as unknown as EmployeeNode[]

  const pageBasedInfo =
    paginationInfo &&
    'type' in paginationInfo &&
    paginationInfo.type === 'pages' &&
    'currentPage' in paginationInfo &&
    'pagesCount' in paginationInfo
      ? paginationInfo
      : null

  return (
    <Box width="full" gap="s8">
      {records.map((node, index) => {
        const employee = employeesById[node.id]
        const isLast = index === records.length - 1
        return (
          <SupplementTableRowCell
            key={node.id}
            employee={employee}
            isLast={isLast}
            supplementsStore={supplementsStore}
            loading={loadingSupplements}
            taxonomies={taxonomies}
            estimatedSalary={
              employee ? estimatedEmployeesSalariesByEmployeeId[employee.id] : undefined
            }
            itemToHighlight={itemToHighlight}
            shouldScrollIntoView={null}
            employeeExpansionState={employeeExpansionState}
            toggleEmployeeExpansion={toggleEmployeeExpansion}
            toggleTaxonomyCreationModal={toggleTaxonomyCreationModal}
          />
        )
      })}
      {pageBasedInfo !== null && (
        <OnePagination
          totalPages={pageBasedInfo.pagesCount}
          currentPage={pageBasedInfo.currentPage}
          onPageChange={setPage}
          disabled={isLoading}
        />
      )}
    </Box>
  )
}
